import { useEffect, useState } from 'react'
import styles from './FrameDrawer.module.scss'
import {
    DotsSixVerticalIcon,
    EyeIcon,
    EyeSlashIcon,
    ArrowsClockwiseIcon,
    CloudArrowUpIcon,
    ArrowSquareOutIcon,
    XIcon,
} from '@phosphor-icons/react'
import { useDispatch, useSelector } from 'react-redux'
import { setShowFrameDrawer } from '../../../store/actions/uiActions'
import { OBJECT_TYPES } from '../types'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import { APPLY_LOADED_OBJECTS_STATE, updateFrameHidden } from '../../../store/actions'
import { Server } from '../../../api/Server'
import {
    ScoopSwitch,
    ScoopCheckbox,
    ScoopText,
    ScoopSpinner,
    ScoopTooltip,
    ScoopButton,
    ScoopDrawer,
    useToast,
} from '../../ui'
import { FrameClass } from './FrameClass'
import { isEqual } from 'lodash'
import { useApi } from '../../../api/api'
import { EVENTS, trackEvent } from '../../../events'
import { useModal, MODALS } from '../../modals'
import { handleUploadSlidesV2, isIntersecting } from '../../screens/Canvas/utils'

export const StrictModeDroppable = ({ children, ...props }) => {
    const [enabled, setEnabled] = useState(false)
    useEffect(() => {
        const animation = requestAnimationFrame(() => setEnabled(true))
        return () => {
            cancelAnimationFrame(animation)
            setEnabled(false)
        }
    }, [])
    if (!enabled) return null
    return <Droppable {...props}>{children}</Droppable>
}

export const FrameDrawer = ({ setReordering, isTourWorkspace }) => {
    const dispatch = useDispatch()
    const toast = useToast()
    const objects = useSelector((state) => state.objects)
    const presentationID = useSelector((state) => state.ui.presentationID)
    const showFrameDrawer = useSelector((state) => state.ui.showFrameDrawer)
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const userID = useSelector((state) => state.auth.userID)
    const token = useSelector((state) => state.auth.token)
    const zoom = useSelector((state) => state.ui.zoom)

    const [isLoading, setIsLoading] = useState(false)
    const [server, setServer] = useState(new Server(workspaceID, userID, token))
    const [selectedCheckboxes, setSelectedCheckboxes] = useState([])
    const [presentationFrames, setPresentationFrames] = useState([])
    const [originalPresentationFrames, setOriginalPresentationFrames] = useState([])
    const allSelected = selectedCheckboxes.length === presentationFrames.length
    const { postData: postThumbnailGenerator } = useApi(
        `https://pig8gecvvk.execute-api.us-west-2.amazonaws.com/corsair/thumbnailgenerator`
    )
    const [canvasID, setCanvasID] = useState(window.location.href.split('/canvas/')[1])
    const slidesModal = useModal(MODALS.CANVAS.SLIDES)

    useEffect(() => {
        const frames = objects
            .filter((obj) => obj.type === OBJECT_TYPES.FRAME)
            .sort((a, b) => a.presentationIndex - b.presentationIndex)
        setPresentationFrames([...frames])
        setOriginalPresentationFrames([...frames])
    }, [objects])

    useEffect(() => {
        setServer(new Server(workspaceID, userID, token))
    }, [userID, token, workspaceID])

    const handleClose = () => {
        dispatch(setShowFrameDrawer(false))
        slidesModal.hide()
    }

    const onDragEnd = (result) => {
        if (!result.destination) return
        const draggedFrame = presentationFrames.filter(
            (frame) => frame.id === parseFloat(result.draggableId.split('-')[1])
        )[0]
        const offset = result.destination.index - draggedFrame.presentationIndex
        setPresentationFrames((prevState) =>
            prevState
                .map((frame) => {
                    if (frame.id === draggedFrame.id)
                        return {
                            ...frame,
                            presentationIndex: result.destination.index,
                        }
                    if (
                        offset > 0 &&
                        frame.presentationIndex > draggedFrame.presentationIndex &&
                        frame.presentationIndex <= result.destination.index
                    )
                        return {
                            ...frame,
                            presentationIndex: frame.presentationIndex - 1,
                        }
                    if (
                        offset < 0 &&
                        frame.presentationIndex < draggedFrame.presentationIndex &&
                        frame.presentationIndex >= result.destination.index
                    )
                        return {
                            ...frame,
                            presentationIndex: frame.presentationIndex + 1,
                        }
                    return frame
                })
                .sort((a, b) => a.presentationIndex - b.presentationIndex)
        )
    }

    const handleOpenSlides = (presentationIndex) => {
        const googleSlidesURL = `https://docs.google.com/presentation/d/${presentationID}/edit#slide=id.p${presentationIndex}`
        window.open(googleSlidesURL, '_blank')
    }

    const getSlideIDFromFrameID = (frameID) => {
        if (!(typeof frameID === 'string')) return frameID
        let index = frameID.indexOf(':')
        if (index > 0) {
            return frameID.substring(0, index)
        } else {
            return frameID
        }
    }

    const doesFrameExist = (presentationIndex) => {
        if (objects.length === 0) return false
        return (
            objects.find(
                (obj) =>
                    obj.type === OBJECT_TYPES.FRAME && obj.presentationIndex === presentationIndex
            ) !== undefined
        )
    }

    const extractBackground = (content) => {
        const regex = /background:([^;]*)/
        const match = content.match(regex)
        if (match) {
            const colorValue = match[1].trim()
            const endIndex = colorValue.indexOf("'")
            return endIndex !== -1 ? colorValue.substring(0, endIndex) : colorValue
        }
        return null
    }

    const handleSyncSlides = () => {
        if (selectedCheckboxes.length === 0) {
            toast.warning('Please select at least one frame to sync')
            return
        }
        const slidesIDs = selectedCheckboxes.map((frameID) => getSlideIDFromFrameID(frameID))
        setIsLoading(true)

        server.postData(
            {
                action: 'getUpdatedSlide',
                presentationID: presentationID,
                slideIDs: allSelected ? undefined : slidesIDs,
            },
            (result) => {
                if (result.error) {
                    toast.error(result.error)
                    setIsLoading(false)
                    return
                }
                let newObjects = [...objects]
                let newFrames = [...objects.filter((obj) => obj.type === OBJECT_TYPES.FRAME)]
                let objectsBySlideID = {}

                for (let obj of result.canvasObjects) {
                    let objSlideID = Number(obj.id.substring(0, obj.id.indexOf(':')))
                    if (!objectsBySlideID[objSlideID]) objectsBySlideID[objSlideID] = []
                    objectsBySlideID[objSlideID].push(obj)
                }

                Object.keys(objectsBySlideID).forEach((slideID) => {
                    objectsBySlideID[slideID].sort((a, b) => {
                        let aSlideID = Number(a.id.substring(a.id.indexOf(':') + 1))
                        let bSlideID = Number(b.id.substring(b.id.indexOf(':') + 1))
                        return aSlideID - bSlideID
                    })
                })

                Object.keys(objectsBySlideID).forEach((slideID) => {
                    if (!doesFrameExist(Number(slideID) - 1)) {
                        const object = objectsBySlideID[slideID][0]
                        const background = extractBackground(object.content)
                        newFrames.push(
                            FrameClass.newFrame(
                                zoom,
                                Number(slideID) - 1,
                                '',
                                object.x,
                                object.y,
                                object.width,
                                object.height,
                                `Slide ${slideID}`,
                                background,
                                Number(slideID)
                            )
                        )
                    }
                })

                const nonImportedObjects = newObjects.filter(
                    (obj) => !result.canvasObjects.some((canvasObj) => canvasObj.id === obj.id)
                )

                newObjects = [...nonImportedObjects, ...result.canvasObjects]
                newObjects = [
                    ...newFrames,
                    ...newObjects.filter((obj) => obj.type !== OBJECT_TYPES.FRAME),
                ]

                dispatch({
                    type: 'APPLY_LOADED_OBJECTS_STATE',
                    payload: newObjects,
                })

                trackEvent(EVENTS.CANVAS.SYNC_FROM_SLIDES, {
                    presentationID,
                })
                setSelectedCheckboxes([])
                setIsLoading(false)
            },
            undefined,
            () => setIsLoading(false)
        )
    }

    const handleSelectAll = () => {
        if (allSelected) {
            setSelectedCheckboxes([])
        } else {
            setSelectedCheckboxes(presentationFrames.map((frame) => frame.id))
        }
    }

    const handleConfirmReorder = () => {
        dispatch(setShowFrameDrawer(false))
        setReordering(true)
        const orderedFrames = [...presentationFrames]
        const frames = objects.filter((obj) => obj.type === OBJECT_TYPES.FRAME)
        const positions = frames
            .map((frame) => ({ x: frame.x, y: frame.y }))
            .sort((a, b) => a.y - b.y || a.x - b.x)
        const importedObjects = objects.filter(
            (obj) => obj.type !== OBJECT_TYPES.FRAME && typeof obj.id === 'string'
        )
        const scoopObjects = objects.filter(
            (obj) => obj.type !== OBJECT_TYPES.FRAME && typeof obj.id !== 'string'
        )
        let resultObjects = []
        orderedFrames.forEach((frame, i) => {
            const oldFramePosition = { x: frame.x, y: frame.y }
            const newFramePosition = { x: positions[i].x, y: positions[i].y }
            if (frame.id % 1 === 0) {
                const importedFrameObjects = importedObjects.filter(
                    (obj) => parseInt(obj.id.split(':')[0]) === frame.id
                )
                const scoopFrameObjects = scoopObjects.filter((obj) => isIntersecting(frame, obj))
                if (!isEqual(oldFramePosition, newFramePosition)) {
                    importedFrameObjects.forEach((o) => {
                        if (!resultObjects.some((i) => i.id === o.id)) {
                            const objOffsetX = o.x - oldFramePosition.x
                            const objOffsetY = o.y - oldFramePosition.y
                            resultObjects = [
                                ...resultObjects,
                                {
                                    ...o,
                                    x: newFramePosition.x + objOffsetX,
                                    y: newFramePosition.y + objOffsetY,
                                },
                            ]
                        }
                    })
                    scoopFrameObjects.forEach((o) => {
                        if (!resultObjects.some((i) => i.id === o.id)) {
                            const objOffsetX = o.x - oldFramePosition.x
                            const objOffsetY = o.y - oldFramePosition.y
                            resultObjects = [
                                ...resultObjects,
                                {
                                    ...o,
                                    x: newFramePosition.x + objOffsetX,
                                    y: newFramePosition.y + objOffsetY,
                                },
                            ]
                        }
                    })
                } else {
                    resultObjects = [
                        ...resultObjects,
                        ...importedFrameObjects,
                        ...scoopFrameObjects,
                    ]
                }
            } else {
                const importedFrameObjects = importedObjects.filter((obj) =>
                    isIntersecting(frame, obj)
                )
                const scoopFrameObjects = scoopObjects.filter((obj) => isIntersecting(frame, obj))
                if (!isEqual(oldFramePosition, newFramePosition)) {
                    importedFrameObjects.forEach((o) => {
                        if (!resultObjects.some((i) => i.id === o.id)) {
                            const objOffsetX = o.x - oldFramePosition.x
                            const objOffsetY = o.y - oldFramePosition.y
                            resultObjects = [
                                ...resultObjects,
                                {
                                    ...o,
                                    x: newFramePosition.x + objOffsetX,
                                    y: newFramePosition.y + objOffsetY,
                                },
                            ]
                        }
                    })
                    scoopFrameObjects.forEach((o) => {
                        if (!isEqual(oldFramePosition, newFramePosition)) {
                            const objOffsetX = o.x - oldFramePosition.x
                            const objOffsetY = o.y - oldFramePosition.y
                            resultObjects = [
                                ...resultObjects,
                                {
                                    ...o,
                                    x: newFramePosition.x + objOffsetX,
                                    y: newFramePosition.y + objOffsetY,
                                },
                            ]
                        }
                    })
                } else {
                    resultObjects = [
                        ...resultObjects,
                        ...importedFrameObjects,
                        ...scoopFrameObjects,
                    ]
                }
            }
            frame.x = newFramePosition.x
            frame.y = newFramePosition.y
        })
        const objectsNotInFrames = scoopObjects.filter(
            (obj) => !orderedFrames.some((frame) => isIntersecting(frame, obj))
        )

        dispatch({
            type: APPLY_LOADED_OBJECTS_STATE,
            payload: [...presentationFrames, ...resultObjects, ...objectsNotInFrames],
        })
        setTimeout(() => setReordering(false), 1000)
    }

    const handleCancelReorder = () => {
        setPresentationFrames([...originalPresentationFrames])
    }

    const isFromPresentation = objects.some((o) => typeof o.id === 'string' && o.id.includes(':'))

    return (
        <>
            {isLoading ? <ScoopSpinner /> : <ScoopText className={'inter'}>Create</ScoopText>}
            <ScoopDrawer anchor="right" open={showFrameDrawer} variant="persistent" width="400px">
                <div className={styles.drawerContent}>
                    <div className={styles.drawerHeader}>
                        <ScoopText className={styles.headerTitle}>Frames</ScoopText>
                        <ScoopButton
                            variant="ghost"
                            onClick={handleClose}
                            className={styles.iconButton}
                        >
                            <XIcon size={20} className={styles.icon} />
                        </ScoopButton>
                    </div>
                    {!isTourWorkspace && (
                        <div className={styles.syncSection}>
                            {isFromPresentation && (
                                <div className={styles.syncFromSlides}>
                                    <ScoopText className={styles.syncTitle}>
                                        Sync from Slides
                                    </ScoopText>
                                    <ScoopTooltip title="Sync changes in Google Slides back to Scoop">
                                        <ScoopButton
                                            variant="ghost"
                                            onClick={() => handleSyncSlides()}
                                            className={styles.iconButton}
                                        >
                                            <ArrowsClockwiseIcon
                                                size={20}
                                                className={styles.icon}
                                            />
                                        </ScoopButton>
                                    </ScoopTooltip>
                                </div>
                            )}
                            <div className={styles.syncToSlides}>
                                <ScoopText className={styles.syncTitle}>Sync to Slides</ScoopText>
                                <ScoopTooltip title="Sync changes back to Google Slides">
                                    <ScoopButton
                                        variant="ghost"
                                        onClick={() =>
                                            handleUploadSlidesV2(
                                                presentationID,
                                                selectedCheckboxes,
                                                toast,
                                                slidesModal,
                                                presentationFrames,
                                                objects,
                                                server,
                                                userID,
                                                workspaceID,
                                                canvasID,
                                                postThumbnailGenerator
                                            )
                                        }
                                        className={styles.iconButton}
                                    >
                                        <CloudArrowUpIcon size={20} className={styles.icon} />
                                    </ScoopButton>
                                </ScoopTooltip>
                            </div>
                        </div>
                    )}
                    {!isTourWorkspace && presentationID && (
                        <div className={styles.selectAllSection}>
                            <ScoopText className={styles.selectAllText}>Select All</ScoopText>
                            <ScoopSwitch checked={allSelected} onChange={handleSelectAll} />
                        </div>
                    )}
                    <div className={styles.drawerBody}>
                        <DragDropContext onDragEnd={onDragEnd}>
                            <StrictModeDroppable droppableId={'droppable'}>
                                {(provided, snapshot) => (
                                    <div {...provided.droppableProps} ref={provided.innerRef}>
                                        {presentationFrames.map((frame, index) => (
                                            <Draggable
                                                key={frame.id}
                                                draggableId={'draggable-' + frame.id}
                                                index={index}
                                            >
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...(isTourWorkspace
                                                            ? {}
                                                            : provided.dragHandleProps)}
                                                        className={`${styles.frameDraggable} ${
                                                            snapshot.isDragging
                                                                ? styles.isDragging
                                                                : ''
                                                        }`}
                                                    >
                                                        {!isTourWorkspace && (
                                                            <DotsSixVerticalIcon
                                                                size={20}
                                                                className={`${styles.icon} ${styles.dragHandle}`}
                                                            />
                                                        )}
                                                        <div className={styles.frameTitle}>
                                                            <ScoopText
                                                                className={styles.frameTitleText}
                                                            >
                                                                {(frame.presentationIndex !==
                                                                undefined
                                                                    ? frame.presentationIndex +
                                                                      1 +
                                                                      '. '
                                                                    : '') + frame.title}
                                                            </ScoopText>
                                                        </div>
                                                        {!isTourWorkspace &&
                                                            isLoading &&
                                                            selectedCheckboxes.includes(
                                                                frame.id
                                                            ) && (
                                                                <ScoopSpinner
                                                                    color="#7442D7"
                                                                    size={36}
                                                                />
                                                            )}
                                                        {!isTourWorkspace &&
                                                            presentationID &&
                                                            isLoading !== frame.id && (
                                                                <>
                                                                    <ScoopTooltip title="Edit original PowerPoint in Google Slides">
                                                                        <ScoopButton
                                                                            variant="ghost"
                                                                            onClick={() =>
                                                                                handleOpenSlides(
                                                                                    frame.presentationIndex +
                                                                                        1
                                                                                )
                                                                            }
                                                                            className={
                                                                                styles.iconButton
                                                                            }
                                                                        >
                                                                            <ArrowSquareOutIcon
                                                                                size={20}
                                                                                className={
                                                                                    styles.icon
                                                                                }
                                                                            />
                                                                        </ScoopButton>
                                                                    </ScoopTooltip>
                                                                    <ScoopTooltip title="Hide / Un-hide this frame in Presentation Mode">
                                                                        <ScoopButton
                                                                            variant="ghost"
                                                                            onClick={() =>
                                                                                dispatch(
                                                                                    updateFrameHidden(
                                                                                        frame.id,
                                                                                        !frame.hidden
                                                                                    )
                                                                                )
                                                                            }
                                                                            className={
                                                                                styles.iconButton
                                                                            }
                                                                        >
                                                                            {frame.hidden ? (
                                                                                <EyeSlashIcon
                                                                                    size={20}
                                                                                    className={
                                                                                        styles.icon
                                                                                    }
                                                                                />
                                                                            ) : (
                                                                                <EyeIcon
                                                                                    size={20}
                                                                                    className={
                                                                                        styles.icon
                                                                                    }
                                                                                />
                                                                            )}
                                                                        </ScoopButton>
                                                                    </ScoopTooltip>
                                                                    <ScoopCheckbox
                                                                        size="sm"
                                                                        checked={selectedCheckboxes.includes(
                                                                            frame.id
                                                                        )}
                                                                        onChange={() => {
                                                                            if (
                                                                                selectedCheckboxes.includes(
                                                                                    frame.id
                                                                                )
                                                                            ) {
                                                                                setSelectedCheckboxes(
                                                                                    selectedCheckboxes.filter(
                                                                                        (item) =>
                                                                                            item !==
                                                                                            frame.id
                                                                                    )
                                                                                )
                                                                            } else {
                                                                                setSelectedCheckboxes(
                                                                                    [
                                                                                        ...selectedCheckboxes,
                                                                                        frame.id,
                                                                                    ]
                                                                                )
                                                                            }
                                                                        }}
                                                                    />
                                                                </>
                                                            )}
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </StrictModeDroppable>
                        </DragDropContext>
                    </div>
                    {!isTourWorkspace &&
                        !isEqual(presentationFrames, originalPresentationFrames) && (
                            <div className={styles.drawerFooter}>
                                <ScoopButton variant="default" onClick={handleConfirmReorder}>
                                    Apply
                                </ScoopButton>
                                <ScoopButton variant="text" onClick={handleCancelReorder}>
                                    Cancel
                                </ScoopButton>
                            </div>
                        )}
                </div>
            </ScoopDrawer>
        </>
    )
}
